import React, { forwardRef, useRef, useState, useEffect, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useMessageFormatter, isMessageDescriptor } from "../../intl/hooks.js";
import "../../intl/shared-resources/serverResponse.js";
import { HighlightOffRounded } from "@mui/icons-material";
import { FormControl, FormControlLabel, Box, TextField, FormHelperText, Fade, InputAdornment, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import "../alerts/InfoMessage.js";
import "../alerts/NoAccessInfoMessage.js";
import { Message, MessageOrientation, MessageType } from "../alerts/Message.js";
import { LocalizedText } from "../i18n/LocalizedText.js";
import "../alerts/InfoAlert.js";
import "../alerts/InfoMessageHoc.js";
import { IconButton } from "../button/Buttons.js";
import "../button/EditButton.js";
import { Icon } from "../icons/Icon.js";
import { CustomIcon } from "../icons/types.js";
import "../icons/ExpandMoreIcon.js";
import { StyledSelect } from "../select2/StyledSelect.js";
import { withStyledInputLabel, StyledInputProps } from "./StyledFormInputHoc.js";
import { validateSpecialCharacters } from "./validation.js";
const useClasses = makeStyles()((theme) => ({
  formInputFullWidth: {
    width: "100%"
  },
  formInputSpacingBottom: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: 0
    }
  },
  formControlLabeled: {
    padding: 0,
    margin: 0,
    justifyContent: "space-between",
    "& > .MuiFormControlLabel-label": {
      marginRight: "auto"
    }
  },
  formControlLabel: {
    ...theme.typography.caption,
    marginBottom: theme.spacing(1),
    marginRight: "auto"
  },
  formControlHelperIcon: {
    marginRight: theme.spacing(0.5)
  },
  formControlErrorWrapper: {
    display: "block"
  },
  formControlHelperText: {
    marginTop: theme.spacing(1),
    textAlign: "right"
  },
  formControlHelperTextLeft: {
    textAlign: "left"
  },
  formControlError: {
    textAlign: "right"
  },
  formControlErrorLeft: {
    textAlign: "left"
  },
  formTextInput: {
    flexGrow: 1,
    width: "100%",
    marginRight: "auto"
  },
  formControlSelect: {
    "& > .MuiInputBase-root:not(.Mui-disabled):before, & > .MuiInputBase-root:hover:not(.Mui-disabled):before, & > .MuiInputBase-root:after": {
      border: "none !important"
    },
    "& > .MuiInputBase-root": {
      height: theme.custom.denseButtonHeight,
      border: `1px solid ${theme.custom.palette.content.liner}`,
      borderRadius: "8px",
      padding: theme.spacing(0.5, 2),
      "& .MuiSelect-select": {
        paddingLeft: theme.spacing(0)
      }
    },
    "& > .MuiInputBase-root.Mui-focused": {
      border: `1px solid ${theme.palette.primary.main} !important`
    },
    "& .MuiSelect-icon": {
      right: "8px"
    }
  },
  textFieldInputProps: {
    ...theme.typography.body2,
    paddingLeft: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: 0,
    height: theme.custom.denseButtonHeight
  },
  multilineTextFieldInputProps: {
    ...theme.typography.body2,
    padding: theme.spacing(1.5, 2)
  },
  formTextInputCleanableFadeHidden: {
    position: "absolute",
    right: theme.spacing(1.5)
  },
  floatingError: {
    ...theme.typography.caption,
    padding: theme.spacing(0.5),
    whiteSpace: "normal"
  }
}));
const localized = defineMessages({
  selectPlaceholder: {
    id: "selectPlaceholder",
    defaultMessage: "Select {field}"
  },
  unsupportedSpecialCharacters: {
    id: "FormInput_unsupportedSpecialCharacters",
    defaultMessage: "Unsupported special characters {value}"
  }
});
class FormInputProps extends StyledInputProps {
  value;
  fieldState;
  onChange;
  disabled;
  variant;
  hideError;
  helperText;
}
const FormTextInput = forwardRef(
  ({
    label,
    value,
    onChange,
    startAdornment,
    endAdornment,
    placeholder,
    fieldState,
    disabled,
    type,
    textAlign = "left",
    hideLabel,
    style,
    variant = "outlined",
    className,
    onKeyDown,
    InputProps,
    helperText,
    hasBottomSpacing = true,
    fullWidth = true,
    inputClassName,
    floatingError,
    ...props
  }, ref) => {
    const { classes, cx } = useClasses();
    const formatMessage = useMessageFormatter();
    const hasError = fieldState?.error?.message?.length > 0;
    const placeholderLocalized = formatMessage(placeholder);
    const { inputProps, ...otherProps } = props;
    const containerRef = useRef(null);
    return /* @__PURE__ */ React.createElement(
      FormControl,
      {
        error: hasError,
        className: cx(
          {
            [classes.formInputFullWidth]: fullWidth,
            [classes.formInputSpacingBottom]: hasBottomSpacing
          },
          className
        ),
        style,
        ref: containerRef
      },
      /* @__PURE__ */ React.createElement(
        FormControlLabel,
        {
          control: /* @__PURE__ */ React.createElement(
            TextField,
            {
              inputRef: ref,
              value: value ?? "",
              onChange,
              onKeyDown,
              placeholder: placeholderLocalized,
              error: hasError,
              className: classes.formTextInput,
              disabled,
              type,
              variant,
              size: "small",
              ...otherProps,
              slotProps: {
                input: {
                  classes: {
                    input: cx(
                      classes.textFieldInputProps,
                      props?.multiline && classes.multilineTextFieldInputProps,
                      inputClassName
                    )
                  },
                  style: { textAlign },
                  startAdornment,
                  endAdornment,
                  inputProps: { "data-testid": "textInput", ...inputProps },
                  ...InputProps
                }
              }
            }
          ),
          label: !hideLabel && label ? /* @__PURE__ */ React.createElement(Box, { className: classes.formControlLabel }, /* @__PURE__ */ React.createElement(LocalizedText, { label })) : null,
          labelPlacement: "top",
          className: classes.formControlLabeled
        }
      ),
      helperText ? /* @__PURE__ */ React.createElement(
        FormHelperText,
        {
          error: false,
          key: "prompt",
          className: `${classes.formControlHelperText} ${textAlign === "left" ? classes.formControlHelperTextLeft : ""}`
        },
        /* @__PURE__ */ React.createElement(Icon, { className: classes.formControlHelperIcon, icon: CustomIcon.WarningOutline }),
        /* @__PURE__ */ React.createElement(LocalizedText, { label: helperText })
      ) : null,
      floatingError ? /* @__PURE__ */ React.createElement(
        Message,
        {
          hideMessage: !hasError,
          containerRef,
          className: classes.floatingError,
          message: fieldState?.error?.message,
          variant: "inherit",
          messageType: MessageType.Error,
          messageOrientation: MessageOrientation.Floating
        }
      ) : hasError ? /* @__PURE__ */ React.createElement(
        FormHelperText,
        {
          key: "error",
          error: true,
          className: `${classes.formControlError} ${textAlign === "left" ? classes.formControlErrorLeft : ""}`
        },
        fieldState?.error?.message
      ) : null
    );
  }
);
const FormTextInputDefault = ({ endAdornment, ...otherParams }) => {
  const isText = isMessageDescriptor(endAdornment) || endAdornment instanceof String;
  return /* @__PURE__ */ React.createElement(
    FormTextInput,
    {
      ref: null,
      ...otherParams,
      endAdornment: isText ? /* @__PURE__ */ React.createElement(InputAdornment, { position: "end" }, /* @__PURE__ */ React.createElement(Typography, { color: "secondary", variant: "body2" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: endAdornment }))) : endAdornment
    }
  );
};
const FormTextInputCleanable = forwardRef(
  ({ clear, value, disallowSpecialCharacters, fieldState, floatingError, ...otherParams }, ref) => {
    const { classes } = useClasses();
    const { formatMessage } = useIntl();
    const { onChange } = otherParams;
    const [currentValue, setCurrentValue] = useState(value);
    const [isSpecialCharactersError, setIsSpecialCharactersError] = useState(false);
    const specialCharactersError = {
      isValidating: false,
      invalid: true,
      isDirty: false,
      isTouched: false,
      error: {
        type: "pattern",
        message: `${formatMessage(localized.unsupportedSpecialCharacters, { value: `'"\\|/()[]{}` })}`
      }
    };
    const isEmpty = !value;
    useEffect(() => {
      setCurrentValue(value);
    }, [value]);
    const clearIcon = useMemo(() => {
      return /* @__PURE__ */ React.createElement(Fade, { in: !isEmpty, appear: false, className: isEmpty ? classes.formTextInputCleanableFadeHidden : "" }, /* @__PURE__ */ React.createElement(InputAdornment, { position: "end" }, /* @__PURE__ */ React.createElement(IconButton, { "data-testid": "textInputClearButton", size: "small", onClick: handleClear }, /* @__PURE__ */ React.createElement(HighlightOffRounded, null))));
    }, [isEmpty]);
    function handleChange(e) {
      const value2 = e.target.value;
      setCurrentValue(value2);
      const isValid = validateSpecialCharacters(value2);
      setIsSpecialCharactersError(!isValid);
      if (isValid) {
        onChange(e);
      }
    }
    function handleClear(e) {
      onChange?.({ target: { value: "" } });
      clear?.(e);
      setIsSpecialCharactersError(false);
      setCurrentValue("");
    }
    return /* @__PURE__ */ React.createElement(
      FormTextInput,
      {
        ref,
        ...otherParams,
        onChange: handleChange,
        value: currentValue,
        endAdornment: clearIcon,
        fieldState: isSpecialCharactersError ? specialCharactersError : fieldState,
        floatingError: floatingError ?? disallowSpecialCharacters
      }
    );
  }
);
const FormSelect = ({
  value,
  options,
  onChange,
  fieldState,
  disabled,
  displayEmpty,
  compareByValue,
  label,
  className,
  dropdownClassName,
  isCompareOptionsAvailable = true,
  hideError,
  alignLeft,
  fullWidth = true,
  hasBottomSpacing = true,
  startAdornment
}) => {
  const { classes, cx } = useClasses();
  const formatMessage = useMessageFormatter();
  const hasError = fieldState?.error?.message?.length > 0;
  const getLabel = (option) => typeof option.label === "string" ? option.label : formatMessage(option.label);
  const compareOptions = (first, second) => {
    return compareByValue ? !isNaN(Number(first.value)) ? Number(first.value) - Number(second.value) : first.value.toString().localeCompare(second.value.toString()) : getLabel(first).localeCompare(getLabel(second));
  };
  const sortedOptions = isCompareOptionsAvailable ? options.sort(compareOptions) : options;
  const emptyOption = { value: "", disabled: true, label: formatMessage(localized.selectPlaceholder, { field: formatMessage(label) }) };
  const allOptions = displayEmpty ? [emptyOption, ...sortedOptions] : sortedOptions;
  return /* @__PURE__ */ React.createElement(
    FormControl,
    {
      error: hasError,
      className: cx(
        {
          [classes.formInputFullWidth]: fullWidth,
          [classes.formInputSpacingBottom]: hasBottomSpacing
        },
        classes.formControlSelect,
        className
      )
    },
    /* @__PURE__ */ React.createElement(
      StyledSelect,
      {
        displayEmpty,
        disabled,
        value: value !== void 0 && value !== null ? value : "",
        onChange: (e) => onChange(e, e.target.value),
        options: allOptions,
        dropdownClassName,
        alignLeft,
        startAdornment
      }
    ),
    hasError && !hideError ? /* @__PURE__ */ React.createElement(FormHelperText, { className: classes.formControlError }, fieldState?.error?.message) : null
  );
};
const FormSelectWithLabel = withStyledInputLabel(FormSelect);
const FormMultilineText = ({ value, onChange, placeholder, fieldState }) => {
  const { classes } = useClasses();
  const formatMessage = useMessageFormatter();
  const hasError = fieldState?.error?.message?.length > 0;
  const placeholderLocalized = formatMessage(placeholder);
  return /* @__PURE__ */ React.createElement(FormControl, { error: hasError, className: classes.formInputFullWidth }, /* @__PURE__ */ React.createElement(
    TextField,
    {
      fullWidth: true,
      multiline: true,
      margin: "dense",
      variant: "outlined",
      placeholder: placeholderLocalized,
      minRows: 2,
      maxRows: 6,
      error: hasError,
      value,
      onChange,
      slotProps: {
        input: {
          classes: {
            input: classes.multilineTextFieldInputProps
          }
        }
      }
    }
  ), hasError ? /* @__PURE__ */ React.createElement(FormHelperText, { className: `${classes.formControlError} ${classes.formControlErrorLeft}` }, fieldState?.error?.message) : null);
};
function FormError({ children, align = "left", ...props }) {
  const { classes, cx } = useClasses();
  return /* @__PURE__ */ React.createElement(FormControl, { "data-testid": props["data-testid"], error: true, className: classes.formControlErrorWrapper }, /* @__PURE__ */ React.createElement(FormHelperText, { className: cx(classes.formControlError, { [classes.formControlErrorLeft]: align === "left" }) }, children));
}
export {
  FormError,
  FormInputProps,
  FormMultilineText,
  FormSelect,
  FormSelectWithLabel,
  FormTextInput,
  FormTextInputCleanable,
  FormTextInputDefault
};
//# sourceMappingURL=FormInput.js.map
